import { getTenantIdByHost, TenantId, themeNameToTenantId } from 'tenants';
import configJSON from 'config.json';
import { isClientSide } from 'utils/host-config';
import { useThemeContext } from 'contexts/theme';

/**
 * Hook to get the current tenant ID.
 * 
 * This hook determines the tenant ID by:
 * 1. First checking the current host using {@link getTenantIdByHost}
 * 2. If that returns the default tenant, tries to get it from theme context
 * 3. Falls back to the default tenant if neither works
 * 
 * @returns The current tenant ID
 */
export function useTenantId(): TenantId {
  const defaultTenantId = configJSON.defaultTenantId as TenantId;
  const { themeName } = useThemeContext();
  
  // 1. First try to get tenant ID from host
  let tenantId: TenantId;
  if (isClientSide()) {
    tenantId = getTenantIdByHost(window.location.host, defaultTenantId)!;
  } else {
    tenantId = defaultTenantId;
  }
  
  // 2. If host lookup returned default, try theme context
  if (tenantId === defaultTenantId && themeName) {
    const tenantIdFromTheme = themeNameToTenantId(themeName);
    if (tenantIdFromTheme) {
      tenantId = tenantIdFromTheme;
    }
  }
  
  // 3. Return the resolved tenant ID (could be from host, theme, or default)
  return tenantId;
}

